import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatApiError, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, FileDown, Mail, Phone, MapPin, User, UserMinus, Search } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";
import ConfirmDialog from "@/components/ConfirmDialog";

const EMPTY_DONOR = {
  first_name: "", middle_name: "", last_name: "",
  email: "", phone: "",
  address_line1: "", address_line2: "",
  city: "", state: "", postal_code: "", country: "",
  notes: "",
  active: true,
};

function DonorDialog({ open, onOpenChange, orgId, donor, onSaved }) {
  const isEdit = !!donor;
  const [form, setForm] = useState(EMPTY_DONOR);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (donor) {
      // Fallback: if first_name not stored, derive from legacy 'name'
      let first = donor.first_name, middle = donor.middle_name, last = donor.last_name;
      if (!first && !last && donor.name) {
        const parts = donor.name.trim().split(/\s+/);
        if (parts.length === 1) { first = parts[0]; }
        else if (parts.length === 2) { first = parts[0]; last = parts[1]; }
        else { first = parts[0]; last = parts[parts.length - 1]; middle = parts.slice(1, -1).join(" "); }
      }
      setForm({
        first_name: first || "",
        middle_name: middle || "",
        last_name: last || "",
        email: donor.email || "",
        phone: donor.phone || "",
        address_line1: donor.address_line1 || "",
        address_line2: donor.address_line2 || "",
        city: donor.city || "",
        state: donor.state || "",
        postal_code: donor.postal_code || "",
        country: donor.country || "",
        notes: donor.notes || "",
        active: donor.active !== false,
      });
    } else if (open) {
      setForm(EMPTY_DONOR);
    }
  }, [donor, open]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.first_name.trim() && !form.last_name.trim()) return toast.error("First or last name is required");
    setSubmitting(true);
    try {
      if (isEdit) await api.patch(`/organizations/${orgId}/donors/${donor.id}`, form);
      else await api.post(`/organizations/${orgId}/donors`, form);
      toast.success(isEdit ? "Donor updated" : "Donor added");
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="donor-dialog" className="rounded-3xl max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Edit donor" : "New donor"}</DialogTitle>
          <DialogDescription>Contact details are used on giving statements.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>First name <span className="text-destructive">*</span></Label><Input data-testid="donor-first-name-input" value={form.first_name} onChange={upd("first_name")} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Middle</Label><Input data-testid="donor-middle-name-input" value={form.middle_name} onChange={upd("middle_name")} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Last name</Label><Input data-testid="donor-last-name-input" value={form.last_name} onChange={upd("last_name")} className="rounded-xl" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="donor-email-input" type="email" value={form.email} onChange={upd("email")} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="donor-phone-input" value={form.phone} onChange={upd("phone")} className="rounded-xl" /></div>
          </div>

          <div className="space-y-3">
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Address</div>
            <div className="space-y-1.5">
              <Label>Street address</Label>
              <Input data-testid="donor-address-line1-input" value={form.address_line1} onChange={upd("address_line1")} placeholder="123 Main St" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Apt, suite, etc. (optional)</Label>
              <Input data-testid="donor-address-line2-input" value={form.address_line2} onChange={upd("address_line2")} placeholder="Apt 4B" className="rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>City</Label><Input data-testid="donor-city-input" value={form.city} onChange={upd("city")} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>State / Province</Label><Input data-testid="donor-state-input" value={form.state} onChange={upd("state")} placeholder="CA" className="rounded-xl" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>ZIP / Postal code</Label><Input data-testid="donor-postal-code-input" value={form.postal_code} onChange={upd("postal_code")} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Country</Label><Input data-testid="donor-country-input" value={form.country} onChange={upd("country")} placeholder="USA" className="rounded-xl" /></div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="donor-notes-input" rows={2} value={form.notes} onChange={upd("notes")} className="rounded-xl" />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-border p-4">
            <div>
              <div className="text-sm font-medium">Active donor</div>
              <div className="text-xs text-muted-foreground">Inactive donors are hidden from "Active only" lists and reports filters.</div>
            </div>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="donor-active-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="donor-save-button" className="rounded-xl">
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Add donor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatementDialog({ open, onOpenChange, orgId, donor }) {
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayISO());

  const download = () => {
    const token = localStorage.getItem("ledger_token") || "";
    const url = `${API_URL}/organizations/${orgId}/reports/donor-statement.pdf?donor_id=${donor.id}&start_date=${start}&end_date=${end}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `giving-statement-${donor.name.replace(/\s+/g, "_")}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success("Statement downloaded");
      })
      .catch(() => toast.error("Failed to generate statement"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="statement-dialog" className="rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Giving statement</DialogTitle>
          <DialogDescription>A polished PDF showing all gifts in the chosen period.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">For <strong className="text-foreground">{donor?.name}</strong></p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="statement-start-date" className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="statement-end-date" className="rounded-xl" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">Close</Button>
          <Button onClick={download} data-testid="statement-download-button" className="rounded-xl"><FileDown className="w-4 h-4 mr-2" />Download PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function composeAddress(d) {
  if (!d) return "";
  const lines = [d.address_line1, d.address_line2];
  const cityLine = [d.city, d.state].filter(Boolean).join(", ");
  const cityZip = [cityLine, d.postal_code].filter(Boolean).join(" ").trim();
  if (cityZip) lines.push(cityZip);
  if (d.country) lines.push(d.country);
  return lines.filter(Boolean).join("\n");
}

export default function DonorsPage() {
  const { activeOrgId } = useAuth();
  const [donors, setDonors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statementOpen, setStatementOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [search, setSearch] = useState("");

  const fetchAll = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/organizations/${activeOrgId}/donors`);
      setDonors(data);
      if (data.length && (!selected || !data.find((d) => d.id === selected.id))) setSelected(data[0]);
      else if (data.length === 0) setSelected(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [activeOrgId]);

  const sweepInactive = async () => {
    const months = window.prompt("Mark donors inactive if they haven't given in how many months?", "12");
    if (!months) return;
    const n = parseInt(months, 10);
    if (!n || n < 1) return toast.error("Please enter a number between 1 and 120");
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/donors/sweep-inactive?months=${n}`);
      toast.success(`Marked ${data.donors_marked_inactive} donor(s) inactive (no giving since ${data.cutoff_date}).`);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filteredDonors = donors.filter((d) => {
    if (statusFilter === "active" && d.active === false) return false;
    if (statusFilter === "inactive" && d.active !== false) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = [d.name, d.email, d.phone, d.city, d.state, d.address_line1].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const activeCount = donors.filter((d) => d.active !== false).length;
  const inactiveCount = donors.filter((d) => d.active === false).length;

  const doDelete = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/organizations/${activeOrgId}/donors/${confirmTarget.id}`);
      toast.success(`"${confirmTarget.name}" deleted`);
      setConfirmTarget(null);
      if (selected?.id === confirmTarget.id) setSelected(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmTarget(null);
    }
  };

  const addressText = composeAddress(selected);

  return (
    <div className="space-y-8 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground">Donors</div>
          <h1 className="font-serif text-5xl tracking-tight mt-2">People who give</h1>
          <p className="text-muted-foreground mt-2 max-w-md">
            Track donors, their gifts, and generate annual giving statements.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={sweepInactive} className="h-11 rounded-2xl" data-testid="sweep-inactive-button">
            <UserMinus className="w-4 h-4 mr-2" /> Sweep inactive
          </Button>
          <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 rounded-2xl soft-glow" data-testid="add-donor-button">
            <Plus className="w-4 h-4 mr-2" /> Add donor
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : donors.length === 0 ? (
        <Card className="border-0 shadow-sm rounded-3xl p-12 text-center">
          <User className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-serif text-2xl mt-4">No donors yet</div>
          <p className="text-muted-foreground mt-2">Add your first donor to start tracking giving.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Card className="lg:col-span-5 border-0 shadow-sm rounded-3xl p-0 overflow-hidden">
            <div className="px-5 pt-4 pb-3 border-b border-border space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="donor-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, phone, city…"
                  className="pl-9 h-10 rounded-xl bg-secondary/40 border-0"
                />
              </div>
              <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList className="bg-secondary/60 rounded-xl h-9 p-0.5 w-full grid grid-cols-3" data-testid="donor-status-tabs">
                  <TabsTrigger value="all" data-testid="donor-filter-all" className="rounded-lg text-xs h-8 data-[state=active]:bg-card">All ({donors.length})</TabsTrigger>
                  <TabsTrigger value="active" data-testid="donor-filter-active" className="rounded-lg text-xs h-8 data-[state=active]:bg-card">Active ({activeCount})</TabsTrigger>
                  <TabsTrigger value="inactive" data-testid="donor-filter-inactive" className="rounded-lg text-xs h-8 data-[state=active]:bg-card">Inactive ({inactiveCount})</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="divide-y divide-border max-h-[600px] overflow-y-auto" data-testid="donors-list">
              {filteredDonors.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">No donors match this filter.</div>
              ) : filteredDonors.map((d) => (
                <button
                  key={d.id}
                  data-testid={`donor-item-${d.id}`}
                  onClick={() => setSelected(d)}
                  className={`w-full text-left px-5 py-4 hover:bg-secondary/40 transition-colors flex items-center justify-between gap-3 ${selected?.id === d.id ? "bg-secondary" : ""}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{d.name}</span>
                      {d.active === false && (
                        <Badge variant="outline" className="bg-muted text-muted-foreground text-[10px] rounded-full border-0">Inactive</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{d.email || d.phone || "—"}</div>
                  </div>
                  <div className="font-mono text-sm tabular-nums text-primary shrink-0">{formatUSD(d.total_given)}</div>
                </button>
              ))}
            </div>
          </Card>

          {selected && (
            <Card className="lg:col-span-7 border-0 shadow-sm rounded-3xl p-7" data-testid="donor-detail-card">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Donor</div>
                  <div className="flex items-center gap-2.5 mt-1.5">
                    <h2 className="font-serif text-4xl">{selected.name}</h2>
                    {selected.active === false && (
                      <Badge variant="outline" className="bg-muted text-muted-foreground rounded-full border-0">Inactive</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(selected); setDialogOpen(true); }} data-testid="donor-edit-button" className="rounded-xl"><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive rounded-xl" onClick={() => setConfirmTarget(selected)} data-testid="donor-delete-button"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {selected.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-muted-foreground" />{selected.email}</div>}
                {selected.phone && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-muted-foreground" />{selected.phone}</div>}
                {addressText && (
                  <div className="flex items-start gap-2 text-sm sm:col-span-2">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <span className="whitespace-pre-line">{addressText}</span>
                  </div>
                )}
              </div>

              <div className="mt-6 p-5 rounded-2xl bg-primary/5 border border-primary/10">
                <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Total Given (all-time)</div>
                <div className="font-serif text-5xl mt-2 tabular-nums text-primary">{formatUSD(selected.total_given)}</div>
              </div>

              {selected.notes && (
                <div className="mt-6">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Notes</div>
                  <p className="text-sm mt-2 text-foreground/80">{selected.notes}</p>
                </div>
              )}

              <div className="mt-7 pt-6 border-t border-border flex justify-end">
                <Button onClick={() => setStatementOpen(true)} data-testid="generate-statement-button" className="rounded-2xl soft-glow">
                  <FileDown className="w-4 h-4 mr-2" /> Generate giving statement (PDF)
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      <DonorDialog open={dialogOpen} onOpenChange={setDialogOpen} orgId={activeOrgId} donor={editing} onSaved={fetchAll} />
      {selected && <StatementDialog open={statementOpen} onOpenChange={setStatementOpen} orgId={activeOrgId} donor={selected} />}

      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title={`Delete "${confirmTarget?.name}"?`}
        description="This will permanently remove this donor. Transactions previously linked to them will keep their amounts but lose the donor reference."
        confirmLabel="Delete donor"
        onConfirm={doDelete}
        testid="confirm-delete-donor"
      />
    </div>
  );
}
